/**
 * A pre-consultation form for a build with no backend behind it.
 *
 * The same arrangement `publicEnquiryPreview` has, and for the same reason:
 * `REACT_APP_FORMS_PREVIEW=true` turns localhost into the forms site, and
 * there is nothing serving these two endpoints yet. Without a stand-in the
 * only screen anybody could look at would be "this link is no longer valid".
 *
 * Test-only in effect — nothing imports this unless `FORMS_PREVIEW` is on, so
 * a production bundle never reaches it. Delete it with the flag.
 */

// The same specimen logo the brand previews and the enquiry form's preview
// use, so the three look like one practitioner rather than three states of
// having uploaded nothing.
import demoLogo from "../../../assets/svg/my brand/demo-logo.png";
import { PRECONSULTATION_FALLBACK_OPTIONS } from "../preconsultationSteps";
import type {
  PreconsultationCatalog,
  PreconsultationForm,
  PreconsultationState,
} from "../types/preconsultation.types";

/**
 * The token a preview build reads a state off.
 *
 * `/i/completed` and `/i/expired` draw those two endings; every other token
 * opens the form. That is the whole of what a preview needs — the three
 * screens exist and can be looked at without a server that can mint a spent
 * link on demand.
 */
export const previewStateOf = (token: string): PreconsultationState =>
  token === "completed"
    ? "COMPLETED"
    : token === "expired"
    ? "EXPIRED"
    : "OPEN";

/** A catalog with ids made up, for a build with no backend to get them from. */
const previewCatalog = (names: readonly string[]): PreconsultationCatalog => ({
  rows: names.map((name, index) => ({ id: index + 1, name })),
  names: [...names],
});

export const previewForm = (token: string): PreconsultationForm => ({
  brand: { name: "Nutritionist Logo", logoUrl: demoLogo },
  state: previewStateOf(token),
  // Ids as well as names, since the submit sends the id behind whatever was
  // picked. Numbered from 1 per list, which is all a preview needs — there is
  // no endpoint behind this build to send them to.
  catalogs: {
    // Made up here and nowhere else. A served form's Goal field offers the
    // practice's own services and falls back on nothing — see `goalRows` —
    // and a preview with no backend behind it has no services to offer, so
    // these four stand in rather than leave the field empty.
    goals: previewCatalog([
      "Weight loss",
      "Weight gain",
      "Muscle gain",
      "General wellness",
    ]),
    lifeStyles: previewCatalog(PRECONSULTATION_FALLBACK_OPTIONS.lifeStyles),
    foodPreferences: previewCatalog(
      PRECONSULTATION_FALLBACK_OPTIONS.foodPreferences
    ),
    cuisines: previewCatalog(PRECONSULTATION_FALLBACK_OPTIONS.cuisines),
    foodAllergies: previewCatalog(
      PRECONSULTATION_FALLBACK_OPTIONS.foodAllergies
    ),
    medicalConditions: previewCatalog(
      PRECONSULTATION_FALLBACK_OPTIONS.medicalConditions
    ),
    // No catalog behind it on a real form either — it goes up as text.
    professions: {
      rows: [],
      names: [...PRECONSULTATION_FALLBACK_OPTIONS.professions],
    },
  },
  // Every section asked, which is what a practitioner who has changed nothing
  // gets. Switch one to `false` here to look at a form with that part left
  // out — see `stepsAsked`.
  fields: {
    location: true,
    foodPreference: true,
    favoriteDishes: true,
    foodsToAvoid: true,
    profession: true,
    dailyRoutine: true,
    lifestyleHabits: true,
    medicalConditions: true,
    medications: true,
  },
  // What a real link carries: the practitioner added this person, so the first
  // step opens on a card of what they already hold rather than on eight boxes
  // — see `PreconsultationBasics`. No date of birth, which is the usual shape
  // of a record added by hand and the one thing the step is really asking for.
  // Empty a field here to see how the card reads a row the record has nothing
  // for.
  known: {
    firstName: "Riya",
    lastName: "Roy",
    age: "26",
    gender: "Female",
    goals: ["Weight loss"],
    lifestyle: "Active",
    email: "riya@gmail.com",
    phone: "7869789636",
  },
  recipientName: "Riya Roy",
});
